import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getIcon } from 'utils/icons';

function StockCard(props: {
  name: string;
  img?: string;
  imgs?: string[];         // array of possible URL strings
  unity: string;
  quantity: number;
  type: string;
  id: string;
  alert: number;
  reference?: string;
  productType?: string;
  color?: string;
}) {
  const navigate = useNavigate();

  // current index of the candidate being used
  const [candidateIndex, setCandidateIndex] = useState(0);

  const {
    name,
    img,
    imgs,
    unity,
    quantity,
    type,
    id,
    alert,
    reference,
    color,
    productType,
  } = props;

  const isProduct = type === 'product';

  // Build an ordered list of candidates (priority: imageUrls / imgs -> imageUrl -> img)
  const candidates = useMemo(() => {
    const arr: string[] = [];
    if (Array.isArray(imgs)) arr.push(...imgs.filter(Boolean));
    if (typeof img === 'string' && img) arr.push(img);
    return arr;
  }, [ imgs, img]);

  // Reset index if the candidates change
  const candidatesKey = useMemo(() => candidates.join('|'), [candidates]);
  useEffect(() => {
    setCandidateIndex(0);
  }, [candidatesKey, type, name]);

  // If it's not a product, we will use the icon (no img)
  const shouldUseIcon = !isProduct && getIcon(name, type);

  const currentSrc = shouldUseIcon
    ? getIcon(name, type)
    : candidates.length > 0 && candidateIndex < candidates.length
    ? candidates[candidateIndex]
    : null;

  // handler if img fails -> try the next one
  const handleImgError = () => {
    // move to the next candidate if it exists
    setCandidateIndex((prev) => {
      const next = prev + 1;
      return next < candidates.length ? next : prev;
    });
  };

  const renderFallback = () => {
    const baseClass =
      'relative h-[75px] w-[75px] text-center rounded-xl flex items-center justify-center';

    if (productType === 'brut') {
      return (
        <div className={`${baseClass} bg-[#d9d9d9]`}>
          <p className="text-white font-bold text-xl">BRUT.</p>
        </div>
      );
    }
    if (productType === 'transformed') {
      return (
        <div className={`${baseClass} bg-[#ffdf59]`}>
          <p className="text-white font-bold text-md">TRANSF.</p>
        </div>
      );
    }
    if (productType === 'assembled') {
      return (
        <div className={`${baseClass} bg-[#7ed956]`}>
          <p className="text-white font-bold text-md">ASSEMB.</p>
        </div>
      );
    }

    return (
      <div className={`${baseClass} bg-[#d9d9d9]`}>
        <p className="text-white font-bold text-xl">
          {name?.[0]?.toUpperCase() || '?'}
        </p>
      </div>
    );
  };

  return (
    <div
      className={`relative flex items-center rounded-[20px] bg-clip-border border px-[18px] py-4 shadow-3xl shadow-shadow-500 hover:cursor-pointer dark:!bg-navy-800 dark:shadow-none `}
      onClick={() => {
        navigate(`${type === 'product' ? '/update-product' : '/update-ingredient'}/${id}`);
      }}
      // onMouseOver={() => setDisplayQR(true)}
      // onMouseLeave={() => setDisplayQR(false)}
    >
      {color && (
        <div
          className={`absolute top-2 left-4 h-2 w-4 bg-opacity-80 rounded-full border`}
          style={{ backgroundColor: color }}
        ></div>
      )}
      <p className="text-xs absolute bottom-2 left-4 text-gray-600">
        {productType === 'transformed' && 'Transformé'}
        {productType === 'assembled' && 'Assemblé'}
        {productType === 'brut' && 'Brut'}
      </p>

      <div className="grid grid-cols-12">
        <div className="-bottom-12 col-span-4 flex h-[87px] w-[87px] items-center justify-center rounded-full">
          {currentSrc ? (
            // Display an image and handle error to try the next one
            <img
              src={currentSrc}
              alt={name}
              className="h-[80px] w-[80px] rounded-xl object-cover"
              onError={handleImgError}
              //onLoad={() => setLoaded(true)}
              referrerPolicy="no-referrer"
              loading="lazy"
            />
          ) : (
            // no candidates => fallback
            renderFallback()
          )}
        </div>

        <div className="col-span-8 ml-4">
          <h2 className={`text-md font-medium leading-6 text-navy-700 dark:text-white`}>{name}</h2>
          <p className="mb-1 text-xs font-medium text-gray-600">{reference ? reference : 'Référence: inconnue'}</p>
          <p className="mb-1 text-sm font-medium text-gray-600">
            Seuil:{' '}
            {quantity <= alert ? (
              <>
                <span className="text-red-500">alerte </span>
                <span className="text-red-500">!</span>
              </>
            ) : (
              <span className="text-green-500">OK</span>
            )}
          </p>
          <h3 className={`text-xl font-bold leading-6 text-navy-700 dark:text-white`}>
            {quantity % 1 === 0 ? quantity : quantity.toFixed(2)} {unity}
          </h3>
        </div>
      </div>
    </div>
  );
}

export default StockCard;
